import type { WidgetVoiceConfig, VoiceStatus } from "./types";
import { parseRecord, stringValue } from "./formatting";

export function normalizedVoiceConfig(value: unknown): WidgetVoiceConfig {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as WidgetVoiceConfig) : {};
}

export function isVoiceAvailable(config: WidgetVoiceConfig): boolean {
  if (!config.enabled || config.provider === "disabled") {
    return false;
  }
  if (config.provider === "gemini-live" || config.provider === "openai-realtime") {
    return Boolean(config.websocketUrl);
  }
  if (config.provider === "vapi") {
    return Boolean(config.vapiPublicKey && config.vapiAssistantId);
  }
  if (config.provider === "elevenlabs") {
    return Boolean(config.websocketUrl || config.elevenLabsSignedUrl);
  }
  return false;
}

export function voiceUnavailableLabel(config: WidgetVoiceConfig): string {
  if (config.provider === "vapi" && config.vapiUnavailableReason) {
    return config.vapiUnavailableReason;
  }
  if (!config.enabled || config.provider === "disabled") {
    return "Voice is off";
  }
  return "Voice is not configured";
}

export function normalizeVoiceStatus(status: string): VoiceStatus {
  if (status === "ready") {
    return "listening";
  }
  if (status === "user_speaking" || status === "no_speech") {
    return "listening";
  }
  if (status === "processing") {
    return "thinking";
  }
  if (
    status === "connecting" ||
    status === "listening" ||
    status === "thinking" ||
    status === "speaking" ||
    status === "turn_complete" ||
    status === "error"
  ) {
    return status;
  }
  return "off";
}

export function voiceProviderStatusError(status: string): string {
  if (status === "microphone_stalled") {
    return "No microphone audio is reaching voice. Check your input device and try again.";
  }
  if (status === "transcription_stalled") {
    return "Voice heard audio but could not transcribe it. Try speaking again.";
  }
  return "";
}

export function voiceStatusLabel(status: VoiceStatus, provider: string): string {
  if (status === "off") {
    return provider ? `${voiceProviderName(provider)} ready` : "";
  }
  if (status === "connecting") {
    return "Connecting voice";
  }
  if (status === "thinking") {
    return "Preparing answer";
  }
  if (status === "speaking") {
    return "Speaking";
  }
  if (status === "listening") {
    return "Listening";
  }
  if (status === "turn_complete") {
    return "Ready";
  }
  return status;
}

export function voiceProviderName(provider: string): string {
  if (provider === "gemini-live") {
    return "Gemini Live";
  }
  if (provider === "openai-realtime") {
    return "GPT Live";
  }
  if (provider === "vapi") {
    return "Vapi";
  }
  if (provider === "elevenlabs") {
    return "ElevenLabs";
  }
  return "Voice";
}

export function vapiTranscript(message: unknown): string {
  const record = parseRecord(message);
  const type = stringValue(record.type);
  const role = stringValue(record.role || record.speaker);
  const transcriptType = stringValue(record.transcriptType || record.transcript_type);
  const text = stringValue(record.transcript || record.text || record.message);
  if (!text || role === "assistant") {
    return "";
  }
  if (type === "transcript" || type === "speech-update" || transcriptType === "final") {
    return transcriptType === "partial" ? "" : text;
  }
  return "";
}

export function floatToPcm16(input: Float32Array, inputSampleRate: number, outputSampleRate: number): Uint8Array {
  const ratio = Math.max(1, inputSampleRate / outputSampleRate);
  const outputLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outputLength);
  for (let index = 0; index < outputLength; index += 1) {
    const sample = input[Math.floor(index * ratio)] ?? 0;
    const clamped = Math.max(-1, Math.min(1, sample));
    output[index] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return new Uint8Array(output.buffer);
}

export function sampleRateFromElevenLabsFormat(format: string, fallback: number): number {
  const match = /^pcm_(\d+)$/.exec(format);
  const parsed = match ? Number(match[1]) : 0;
  return Number.isFinite(parsed) && parsed >= 8000 && parsed <= 48000 ? parsed : fallback;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
